import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/axios';
import '../styles/style.css';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setBusy(true);
    try {
      const res = await api.post(`/auth/reset-password/${token}`, { newPassword, confirmPassword });
      setMessage(res.data.message);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'This reset link is invalid or has expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="brand-panel">
        <div className="brand-glow" />
        <div className="brand-content">
          <div className="wordmark">HirePeaks</div>
          <div className="eyebrow">OFFER LETTER SYSTEM</div>
          <p className="tagline">
            Every offer, drafted and sent with the same care it took to earn it.
          </p>
        </div>
        <div className="brand-footer">Internal tool · Authorized staff only</div>
      </div>

      <div className="form-panel">
        <form className="login-card" onSubmit={handleSubmit} noValidate>
          <h1 className="login-title">Reset password</h1>
          <p className="login-subtitle">Enter your new password below</p>

          {error && (
            <div className="error-box" role="alert">
              <ErrorIcon />
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className="success-box" role="status">
              <span>{message}</span>
            </div>
          )}

          <div className="field-group">
            <label className="field-label" htmlFor="rp-new">New Password</label>
            <div className="password-wrap">
              <input
                id="rp-new"
                className="hp-input hp-input--password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoFocus
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="hp-eye-btn"
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div className="field-group">
            <label className="field-label" htmlFor="rp-confirm">Confirm New Password</label>
            <input
              id="rp-confirm"
              className="hp-input"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />
          </div>

          <button type="submit" disabled={busy} className="hp-submit">
            {busy ? (
              <>
                <span className="hp-spinner" aria-hidden="true" />
                Resetting…
              </>
            ) : (
              'Reset Password'
            )}
          </button>

          <Link to="/login" className="back-to-login">
            ← Back to sign in
          </Link>
        </form>
      </div>
    </div>
  );
}

function ErrorIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="10" cy="10" r="9" stroke="#C0362A" strokeWidth="1.6" />
      <line x1="10" y1="6" x2="10" y2="11" stroke="#C0362A" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="10" cy="14" r="1" fill="#C0362A" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" stroke="#64748B" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="3" stroke="#64748B" strokeWidth="1.6" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 3l18 18" stroke="#64748B" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M10.6 5.1A11 11 0 0 1 12 5c7 0 11 7 11 7a17.6 17.6 0 0 1-3.4 4.3M6.6 6.6C3.7 8.3 1 12 1 12s4 7 11 7a10.6 10.6 0 0 0 5-1.2" stroke="#64748B" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M9.5 9.6a3 3 0 0 0 4.2 4.2" stroke="#64748B" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
